import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAppContext } from "../../contexts/AppContext";
import { Minus, Plus, Trash2, ArrowRight, ShoppingBag, Loader2, ArrowLeft } from "lucide-react";
import { API_URL } from "../../constant";

export default function PurchaseCartPage() {
  const { cartItems, updateCartQty, removeFromCart, user, setLoginModalOpen, clearCart, showToast, refreshPurchasedCards } = useAppContext();
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const processedSession = useRef(null);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(location.search);
      const sessionId = params.get("session_id");
      
      if (sessionId && user?.access_token && processedSession.current !== sessionId) {
        processedSession.current = sessionId;
        
        // Prevent reprocessing across component remounts or back button usage
        if (sessionStorage.getItem(`processed_session_${sessionId}`)) {
          navigate("/purchase/cart", { replace: true });
          return;
        }

        setIsProcessing(true);
        const apiUrl = API_URL;
        fetch(`${apiUrl}/api/checkout/success`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "Authorization": `Bearer ${user.access_token}`
          },
          body: JSON.stringify({ session_id: sessionId })
        })
        .then(res => res.json())
        .then(data => {
          if (data.status === 'success') {
            sessionStorage.setItem(`processed_session_${sessionId}`, 'true');
            clearCart();
            refreshPurchasedCards();
            showToast("Payment successful!");
            navigate("/purchase/cart", { replace: true });
          } else {
            console.warn("Payment status: ", data.message);
          }
        })
        .catch(err => {
          console.error("Error verifying payment:", err);
        })
        .finally(() => {
          setIsProcessing(false);
        });
      }
    }
  }, [user, clearCart, showToast, navigate, location.search, refreshPurchasedCards]);

  const cartTotal = cartItems.reduce((acc, item) => acc + item.price * item.qty, 0);

  const getTopCoins = (price) => {
    if (price === 5) return 2;
    if (price === 10) return 4;
    if (price === 25) return 8;
    return price;
  };
  const topCoinsTotal = cartItems.reduce((acc, item) => acc + getTopCoins(item.price) * item.qty, 0);

  const handleCheckout = async () => {
    if (!user) {
      setLoginModalOpen(true);
      return;
    }
    
    setIsProcessing(true);
    
    try {
      const apiUrl = API_URL;
      const res = await fetch(`${apiUrl}/api/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: cartItems,
          success_url: window.location.origin + "/purchase/cart",
          cancel_url: window.location.origin + "/purchase/cart?canceled=true",
        })
      });

      const data = await res.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        showToast("Checkout failed: " + (data.error || "Unknown error"));
      }
    } catch (err) {
      showToast("Error initiating checkout. Is the backend running?");
      console.error(err);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="purchase-cart-page" style={{ backgroundColor: 'rgb(249, 250, 251)', minHeight: '100vh', margin: 0, padding: 0 }}>
      <div className="max-w-7xl mx-auto px-4 py-8 md:px-8" style={{ maxWidth: '80rem', margin: '0 auto', padding: '2rem 1rem', minHeight: '60vh' }}>
        {/* Go Back Button */}
        <div className="mb-6" style={{ marginBottom: '1.5rem' }}>
          <Link 
            to="/purchase" 
            className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-[#03173d] transition-colors"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#4b5563', textDecoration: 'none' }}
          >
            <ArrowLeft className="w-4 h-4" style={{ width: '1rem', height: '1rem' }} />
            Go Back
          </Link>
        </div>

        <h1 className="text-3xl font-black text-[#03173d] mb-8 flex items-center gap-3" style={{ fontSize: '1.875rem', fontWeight: 900, color: '#03173d', marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <ShoppingBag className="w-8 h-8 text-amber-500" style={{ width: '2rem', height: '2rem', color: '#f59e0b' }} />
          Your Shopping Cart
        </h1>

        {cartItems.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-12 text-center flex flex-col items-center justify-center" style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '0.75rem', padding: '3rem', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <div className="bg-gray-200 p-4 rounded-full mb-4" style={{ backgroundColor: '#e5e7eb', padding: '1rem', borderRadius: '9999px', marginBottom: '1rem' }}>
              <ShoppingBag className="w-12 h-12 text-gray-400" style={{ width: '3rem', height: '3rem', color: '#9ca3af' }} />
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2" style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1f2937', margin: '0 0 0.5rem' }}>Your cart is empty</h2>
            <p className="text-gray-500 mb-6" style={{ color: '#6b7280', margin: '0 0 1.5rem' }}>Looks like you haven't added any Captain TC cards yet.</p>
            <Link 
              to="/purchase"
              className="bg-[#03173d] hover:bg-[#072255] text-white font-bold py-3 px-8 rounded-lg shadow-md transition-colors"
              style={{ backgroundColor: '#03173d', color: '#fff', fontWeight: 700, padding: '0.75rem 2rem', borderRadius: '0.5rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', textDecoration: 'none' }}
            >
              Start Shopping
            </Link>
          </div>
        ) : (
          <div className="flex flex-col gap-8" style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
            
            {/* Cart Items List */}
            <div className="space-y-4" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
              {cartItems.map((item) => (
                <div 
                  key={item.id} 
                  className="flex flex-col sm:flex-row items-center gap-6 bg-white border border-gray-200 rounded-xl p-4 md:p-6 shadow-sm"
                  style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '0.75rem', padding: '1.5rem', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)' }}
                >
                  {/* Product Image */}
                  <div className="relative w-24 h-32 flex-shrink-0 bg-black rounded-lg overflow-hidden border border-amber-300" style={{ position: 'relative', width: '6rem', height: '8rem', flexShrink: 0, backgroundColor: '#000', borderRadius: '0.5rem', overflow: 'hidden', border: '1px solid #fcd34d' }}>
                    {item.image ? (
                      <img src={item.image} alt={item.name} className="object-cover" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xs text-gray-500" style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', color: '#6b7280' }}>No Image</div>
                    )}
                  </div>

                  {/* Product Details */}
                  <div className="flex-1 flex flex-col justify-between self-stretch w-full" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignSelf: 'stretch', width: '100%' }}>
                    <div className="flex justify-between items-start" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <h3 className="text-lg font-bold text-[#03173d]" style={{ fontSize: '1.125rem', fontWeight: 700, color: '#03173d', margin: 0 }}>{item.name}</h3>
                        <p className="text-sm text-gray-500 mt-1" style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0.25rem 0 0' }}>Earn {getTopCoins(item.price)} TopCoins with this purchase</p>
                      </div>
                      <div className="text-xl font-black text-[#03173d]" style={{ fontSize: '1.25rem', fontWeight: 900, color: '#03173d' }}>
                        £{item.price.toFixed(2)}
                      </div>
                    </div>

                    {/* Quantity and Actions */}
                    <div className="flex items-center justify-between mt-4" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '1rem' }}>
                      <div className="flex items-center border border-gray-300 rounded-lg" style={{ display: 'flex', alignItems: 'center', border: '1px solid #d1d5db', borderRadius: '0.5rem' }}>
                        <button 
                          onClick={() => updateCartQty(item.id, item.qty - 1)}
                          className="px-3 py-2 text-gray-500 hover:text-[#03173d] hover:bg-gray-100 transition-colors"
                          disabled={item.qty <= 1}
                          style={{ padding: '0.5rem 0.75rem', color: '#6b7280', background: 'none', border: 'none', cursor: item.qty <= 1 ? 'not-allowed' : 'pointer', opacity: item.qty <= 1 ? 0.5 : 1 }}
                        >
                          <Minus className="w-4 h-4" style={{ width: '1rem', height: '1rem' }} />
                        </button>
                        <span className="w-12 text-center font-bold text-[#03173d]" style={{ width: '3rem', textAlign: 'center', fontWeight: 700, color: '#03173d' }}>{item.qty}</span>
                        <button 
                          onClick={() => updateCartQty(item.id, item.qty + 1)}
                          className="px-3 py-2 text-gray-500 hover:text-[#03173d] hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={item.qty >= 1}
                          style={{ padding: '0.5rem 0.75rem', color: '#6b7280', background: 'none', border: 'none', cursor: item.qty >= 1 ? 'not-allowed' : 'pointer', opacity: item.qty >= 1 ? 0.5 : 1 }}
                        >
                          <Plus className="w-4 h-4" style={{ width: '1rem', height: '1rem' }} />
                        </button>
                      </div>

                      <button 
                        onClick={() => removeFromCart(item.id)}
                        className="text-red-500 hover:text-red-700 flex items-center gap-1.5 text-sm font-semibold transition-colors bg-red-50 hover:bg-red-100 px-3 py-2 rounded-md"
                        style={{ color: '#ef4444', backgroundColor: '#fef2f2', display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.875rem', fontWeight: 600, padding: '0.5rem 0.75rem', borderRadius: '0.375rem', border: 'none', cursor: 'pointer' }}
                      >
                        <Trash2 className="w-4 h-4" style={{ width: '1rem', height: '1rem' }} />
                        <span className="hidden sm:inline">Remove</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div style={{ width: '100%' }}>
              <div className="bg-[#f9fafb] border border-gray-200 rounded-xl p-6 shadow-sm sticky top-8" style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '0.75rem', padding: '1.5rem', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', position: 'sticky', top: '2rem' }}>
                <h3 className="text-xl font-black text-[#03173d] mb-4" style={{ fontSize: '1.25rem', fontWeight: 900, color: '#03173d', margin: '0 0 1rem' }}>Order Summary</h3>
                
                <div className="space-y-3 mb-6" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', margin: '0 0 1.5rem' }}>
                  <div className="flex justify-between text-gray-600" style={{ display: 'flex', justifyContent: 'space-between', color: '#4b5563' }}>
                    <span>Subtotal</span>
                    <span className="font-semibold text-gray-900" style={{ fontWeight: 600, color: '#111827' }}>£{cartTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600" style={{ display: 'flex', justifyContent: 'space-between', color: '#4b5563' }}>
                    <span>Shipping</span>
                    <span className="font-semibold text-gray-900" style={{ fontWeight: 600, color: '#111827' }}>Calculated at checkout</span>
                  </div>
                  <div className="flex justify-between text-amber-600 font-medium" style={{ display: 'flex', justifyContent: 'space-between', color: '#d97706', fontWeight: 500 }}>
                    <span>TopCoins to Earn</span>
                    <span>+{topCoinsTotal}</span>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4 mb-6" style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1rem', margin: '0 0 1.5rem' }}>
                  <div className="flex justify-between items-center text-xl font-black text-[#03173d]" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '1.25rem', fontWeight: 900, color: '#03173d' }}>
                    <span>Total</span>
                    <span>£{cartTotal.toFixed(2)}</span>
                  </div>
                </div>

                <button 
                  onClick={handleCheckout}
                  disabled={isProcessing}
                  className="w-full text-[#03173d] font-bold py-3.5 px-6 rounded-lg shadow-md transition-all flex items-center justify-center gap-2 group disabled:opacity-70 disabled:cursor-not-allowed"
                  style={{ width: '100%', background: 'linear-gradient(to right, #fbbf24, #f59e0b)', color: '#03173d', fontWeight: 700, padding: '0.875rem 1.5rem', borderRadius: '0.5rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', border: 'none', cursor: isProcessing ? 'not-allowed' : 'pointer', opacity: isProcessing ? 0.7 : 1 }}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" style={{ width: '1.25rem', height: '1.25rem' }} />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <span>Proceed to Checkout</span>
                      <ArrowRight className="w-5 h-5" style={{ width: '1.25rem', height: '1.25rem' }} />
                    </>
                  )}
                </button>

                <div className="mt-4 text-center" style={{ marginTop: '1rem', textAlign: 'center' }}>
                  <Link to="/purchase" className="text-sm font-semibold text-[#03173d] hover:underline" style={{ fontSize: '0.875rem', fontWeight: 600, color: '#03173d', textDecoration: 'none' }}>
                    Or continue shopping
                  </Link>
                </div>
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
}
